import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";
import { he } from "date-fns/locale";
import { paths } from "../../routes/paths";
import REMINDERS from "../../api/services/REMINDERS";
import styles from "../../styles/_containers/reminderBanner.module.scss";

const MAX_VISIBLE = 3;

const SNOOZE_OPTIONS = [
  { label: "15 דקות", minutes: 15 },
  { label: "שעה", minutes: 60 },
  { label: "מחר", minutes: 1440 },
];

const ReminderBanner = () => {
  const [reminders, setReminders] = useState([]);
  const [snoozeOpen, setSnoozeOpen] = useState(null); // reminder id with open dropdown
  const navigate = useNavigate();

  const fetchTriggered = useCallback(async () => {
    try {
      const data = await REMINDERS.get_triggered();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.reminders)
        ? data.reminders
        : [];
      setReminders(list.slice(0, MAX_VISIBLE));
    } catch (err) {
      // Silently fail — banner is non-critical
      console.log("ReminderBanner fetch error:", err);
    }
  }, []);

  useEffect(() => {
    fetchTriggered();
    // Poll every 60 seconds
    const interval = setInterval(fetchTriggered, 60_000);
    return () => clearInterval(interval);
  }, [fetchTriggered]);

  const dismiss = (id) => {
    setReminders((prev) => prev.filter((r) => r._id !== id));
  };

  const handleComplete = async (id) => {
    try {
      await REMINDERS.complete_reminder(id);
      toast.success("תזכורת סומנה כבוצע");
      dismiss(id);
    } catch (err) {
      toast.error("שגיאה בעדכון התזכורת");
    }
  };

  const handleSnooze = async (id, minutes) => {
    try {
      const snoozeUntil = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      await REMINDERS.snooze_reminder(id, { snoozeUntil });
      toast.success("התזכורת נדחתה");
      dismiss(id);
    } catch (err) {
      toast.error("שגיאה בדחיית התזכורת");
    }
    setSnoozeOpen(null);
  };

  if (reminders.length === 0) return null;

  return (
    <div className={styles.banner} dir="rtl">
      {reminders.map((reminder) => (
        <div key={reminder._id} className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.cardTop}>
              <span className={styles.bell}>🔔</span>
              <span className={styles.title}>{reminder.title}</span>
            </div>
            <div className={styles.cardMeta}>
              {reminder.dueDate && (
                <span className={styles.timeAgo}>
                  {formatDistanceToNow(new Date(reminder.dueDate), {
                    addSuffix: true,
                    locale: he,
                  })}
                </span>
              )}
              {reminder.clientName && (
                <span className={styles.clientName}>{reminder.clientName}</span>
              )}
            </div>
          </div>

          <div className={styles.cardActions}>
            {/* Complete */}
            <button
              className={`${styles.actionBtn} ${styles.completeBtn}`}
              onClick={() => handleComplete(reminder._id)}
            >
              בוצע
            </button>

            {/* Snooze dropdown */}
            <div className={styles.snoozeWrap}>
              <button
                className={`${styles.actionBtn} ${styles.snoozeBtn}`}
                onClick={() =>
                  setSnoozeOpen(snoozeOpen === reminder._id ? null : reminder._id)
                }
              >
                דחה
              </button>
              {snoozeOpen === reminder._id && (
                <div className={styles.snoozeDropdown}>
                  {SNOOZE_OPTIONS.map((opt) => (
                    <button
                      key={opt.minutes}
                      className={styles.snoozeOption}
                      onClick={() => handleSnooze(reminder._id, opt.minutes)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Open client */}
            {reminder.clientId && (
              <button
                className={`${styles.actionBtn} ${styles.clientBtn}`}
                onClick={() => navigate(paths.clientMemberShip(reminder.clientId))}
              >
                פתח לקוח
              </button>
            )}

            {/* Dismiss */}
            <button
              className={styles.dismissBtn}
              onClick={() => dismiss(reminder._id)}
              aria-label="סגור"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReminderBanner;
